/**
 * Renders the actions for managing a SIM card detail.
 *
 * @component
 * @param {ESimInfo[]} esimInfo - The eSIM information.
 * @param {string} simId - The SIM card ID.
 * @returns {JSX.Element} The rendered component.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { SectionButtonHeader } from "@/components/Interface/SectionButtonHeader/SectionButtonHeader";
import { ESimInfo } from "@/services/model";
import {
  checkIfDeviceCanReloadEsim,
  checkIfDeviceCanTransferEsim,
} from "@/utils/deviceUtils";

import { EsimDisabledActionsTooltip } from "./EsimDisabledActionsTooltip/EsimDisabledActionsTooltip";

export const SimDetailActions = ({
  esimInfo,
  simId,
}: {
  esimInfo: ESimInfo[];
  simId: string;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details",
  });
  const canReloadEsim = checkIfDeviceCanReloadEsim(esimInfo);
  const canChangeEsim = checkIfDeviceCanTransferEsim(esimInfo);

  const esimReloadDisabledReasons =
    esimInfo?.[0]?.esimResetNotAllowedReason || [];

  const esimTransferDisabledReasons =
    esimInfo?.[0]?.esimTransferNotAllowedReason || [];

  return (
    <div data-testid="manage-esim" id="manage-esim">
      <div>
        <SectionButtonHeader
          className="!mb-0 font-semibold"
          header={t("reloadEsim.title")}
        >
          {canReloadEsim ? (
            <Link
              className="change-button font-normal"
              to={`../reload?simId=${simId}`}
              replace
              data-testid="reload-esim-button"
            >
              {t("reloadEsim.button")}
            </Link>
          ) : (
            <EsimDisabledActionsTooltip
              reasons={esimReloadDisabledReasons}
              disabledButtonText={t("reloadEsim.button")}
            />
          )}
        </SectionButtonHeader>
        <p className="text-xs leading-6">{t("reloadEsim.text")}</p>
      </div>
      <div>
        <SectionButtonHeader
          className="!mb-0 font-semibold"
          header={t("transferEsim.title")}
        >
          {canChangeEsim ? (
            <Link
              className="change-button font-normal"
              to={`../transfer?simId=${simId}`}
              replace
              data-testid="transfer-esim-button"
            >
              {t("transferEsim.button")}
            </Link>
          ) : (
            <EsimDisabledActionsTooltip
              reasons={esimTransferDisabledReasons}
              disabledButtonText={t("transferEsim.button")}
            />
          )}
        </SectionButtonHeader>
        <p className="text-xs leading-6">{t("transferEsim.text")}</p>
      </div>
    </div>
  );
};
